import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { useRecoilValue } from "recoil";
import {
  matrixClient,
  IMatrixClient,
  MatrixMessage,
  MatrixRoomInfo,
  MatrixMember,
  MatrixSyncResponse,
  MatrixRawEvent,
} from "../../services/matrixClient";
import { getVisibleRooms } from "../../services/roomListing";
import { selfUserAtom } from "../../atoms";
import { ADMIN_ROLE } from "../../constants";

/**
 * MatrixProvider — React context for Matrix client state
 *
 * Handles authentication, room management, message state,
 * real-time sync loop, typing indicators, read receipts,
 * reactions, replies, pins, file uploads, and forwarding.
 */

// ==================== TYPES ====================

interface TypingState {
  [roomId: string]: string[];
}

interface ReadReceiptState {
  [roomId: string]: Record<string, string>;
}

// Track reactions: messageEventId → { emoji → { reactionEventId, senderUserId }[] }
interface ReactionInfo {
  reactionEventId: string;
  sender: string;
}

interface ReactionsState {
  [roomId: string]: Record<string, Record<string, ReactionInfo[]>>;
}

interface PinnedState {
  [roomId: string]: string[];
}

export interface MatrixContextType {
  client: IMatrixClient;
  isLoggedIn: boolean;
  userId: string | null;
  currentRoom: MatrixRoomInfo | null;
  currentRoomMembers: MatrixMember[];
  messages: MatrixMessage[];
  rooms: MatrixRoomInfo[];
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;
  typingUsers: string[];
  readReceipts: Record<string, string>;
  reactions: Record<string, Record<string, ReactionInfo[]>>;
  pinnedEventIds: string[];

  // Auth
  login: (user: string, password: string) => Promise<void>;
  logout: () => void;

  // Room actions
  setCurrentRoom: (roomId: string) => Promise<void>;
  refreshRooms: () => Promise<void>;

  // Message actions
  sendMessage: (content: string) => Promise<void>;
  editMessage: (eventId: string, newContent: string) => Promise<void>;
  deleteMessage: (eventId: string, reason?: string) => Promise<void>;
  loadMoreMessages: () => Promise<void>;

  // Replies
  sendReply: (parentEventId: string, content: string) => Promise<void>;

  // Reactions
  sendReaction: (eventId: string, emoji: string) => Promise<void>;
  removeReaction: (reactionEventId: string) => Promise<void>;

  // Pins
  pinMessage: (eventId: string) => Promise<void>;
  unpinMessage: (eventId: string) => Promise<void>;

  // File upload
  uploadAndSendFile: (file: File) => Promise<void>;

  // Forward
  forwardMessage: (toRoomId: string, content: string) => Promise<void>;

  // Typing
  sendTyping: (isTyping: boolean) => void;

  // Read receipts
  markRead: (eventId: string) => void;
}

const MatrixContext = createContext<MatrixContextType | undefined>(undefined);

// ==================== PROVIDER ====================

interface MatrixProviderProps {
  children: ReactNode;
  autoLogin?: { user: string; password: string };
}

export const MatrixProvider: React.FC<MatrixProviderProps> = ({
  children,
  autoLogin,
}) => {
  const selfUser = useRecoilValue(selfUserAtom);
  const [isLoggedIn, setIsLoggedIn] = useState(matrixClient.isLoggedIn());
  const [userId, setUserId] = useState<string | null>(matrixClient.getUserId());
  const [currentRoom, setCurrentRoomState] = useState<MatrixRoomInfo | null>(null);
  const [currentRoomMembers, setCurrentRoomMembers] = useState<MatrixMember[]>([]);
  const [messages, setMessages] = useState<MatrixMessage[]>([]);
  const [rooms, setRooms] = useState<MatrixRoomInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typingState, setTypingState] = useState<TypingState>({});
  const [readReceiptState, setReadReceiptState] = useState<ReadReceiptState>({});
  const [reactionsState, setReactionsState] = useState<ReactionsState>({});
  const [pinnedState, setPinnedState] = useState<PinnedState>({});

  const syncTokenRef = useRef<string | null>(null);
  const syncRunningRef = useRef(false);
  const currentRoomRef = useRef<string | null>(null);
  const paginationTokenRef = useRef<string | undefined>(undefined);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Auth ─────────────────────────────────────

  const login = useCallback(async (user: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const resp = await matrixClient.login(user, password);
      setIsLoggedIn(true);
      setUserId(resp.matrixUserId);
    } catch (err: any) {
      const msg =
        err?.response?.data?.error || err.message || "Login failed";
      setError(msg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    matrixClient.logout();
    setIsLoggedIn(false);
    setUserId(null);
    setMessages([]);
    setRooms([]);
    setCurrentRoomState(null);
    syncTokenRef.current = null;
  }, []);

  // ── Auto-login ───────────────────────────────

  useEffect(() => {
    if (autoLogin && !isLoggedIn) {
      login(autoLogin.user, autoLogin.password).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Resolve Matrix userId in backend mode ────
  useEffect(() => {
    if (matrixClient.mode !== "backend" || userId) return;
    (async () => {
      try {
        const { API } = await import("../../queries/api");
        const { data: user } = await API.get("user");
        if (user?.id) {
          const { data: tokenResp } = await API.post("matrix/token", { userId: user.id });
          // CommonResultDTO wraps in .result, try all paths
          const mid =
            tokenResp?.result?.userId ||
            tokenResp?.result?.matrixUserId ||
            tokenResp?.matrixUserId ||
            tokenResp?.userId;
          if (mid) {
            setUserId(mid);
          }
        }
      } catch (err) {
        console.warn("Failed to resolve Matrix userId:", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Room Management ──────────────────────────

  const refreshRooms = useCallback(async () => {
    if (!matrixClient.isLoggedIn()) return;
    try {
      // matrix/rooms is super-admin-only: club roles get their club-scoped list
      const roomList = await getVisibleRooms(matrixClient, {
        isSuperAdmin: selfUser?.role === ADMIN_ROLE.ADMIN,
        clubId: selfUser?.idClub,
      });
      setRooms(roomList);
    } catch (err: any) {
      console.error("Failed to refresh rooms:", err);
    }
  }, [selfUser?.role, selfUser?.idClub]);

  const setCurrentRoom = useCallback(async (roomId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      setMessages([]);
      paginationTokenRef.current = undefined;
      currentRoomRef.current = roomId;

      const roomInfo = await matrixClient.getRoomInfo(roomId);
      setCurrentRoomState(roomInfo);

      // Load initial messages
      const result = await matrixClient.getMessages(roomId, 50);
      setMessages(result.messages);
      paginationTokenRef.current = result.end;

      // Load members
      try {
        const members = await matrixClient.getMembers(roomId);
        setCurrentRoomMembers(members);
      } catch {
        setCurrentRoomMembers([]);
      }

      // Load pinned messages
      try {
        const pins = await matrixClient.getPinnedMessages(roomId);
        setPinnedState((prev) => ({ ...prev, [roomId]: pins }));
      } catch {
        // non-critical
      }
    } catch (err: any) {
      console.error("Failed to set current room:", err);
      setError(err?.response?.data?.error || err.message || "Failed to load room");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMoreMessages = useCallback(async () => {
    if (!currentRoomRef.current || !paginationTokenRef.current) return;
    try {
      setIsLoading(true);
      const result = await matrixClient.getMessages(
        currentRoomRef.current,
        50,
        paginationTokenRef.current
      );
      setMessages((prev) => [...result.messages, ...prev]);
      paginationTokenRef.current = result.end;
    } catch (err: any) {
      console.error("Failed to load more messages:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── Backend message merge helper ─────────────
  const mergeMessages = useCallback(
    (existing: MatrixMessage[], incoming: MatrixMessage[]): MatrixMessage[] => {
      const msgMap = new Map<string, MatrixMessage>();
      for (const m of existing) msgMap.set(m.eventId, m);
      for (const m of incoming) msgMap.set(m.eventId, m);
      const merged = Array.from(msgMap.values()).sort(
        (a, b) => a.timestamp - b.timestamp
      );
      if (
        merged.length === existing.length &&
        merged.every((m, i) => m.eventId === existing[i].eventId)
      ) {
        return existing; // no change — avoid re-render
      }
      return merged;
    },
    []
  );

  // ── Message Actions ──────────────────────────

  const sendMessage = useCallback(
    async (content: string) => {
      if (!currentRoomRef.current) throw new Error("No room selected");
      const roomId = currentRoomRef.current;
      try {
        const eventId = await matrixClient.sendMessage(roomId, content);
        if (matrixClient.mode === "backend") {
          // No optimistic update in backend mode — avoids echo caused by
          // eventId format mismatch between send response and getMessages.
          // Immediately re-fetch so the sent message appears quickly.
          try {
            const result = await matrixClient.getMessages(roomId, 50);
            if (currentRoomRef.current === roomId) {
              setMessages((prev) => mergeMessages(prev, result.messages));
            }
          } catch {
            /* next poll will pick it up */
          }
        } else {
          const optimistic: MatrixMessage = {
            eventId,
            roomId,
            sender: userId || "self",
            senderDisplayName: userId || "You",
            content,
            timestamp: Date.now(),
            type: "m.room.message",
            msgtype: "m.text",
          };
          setMessages((prev) => [...prev, optimistic]);
        }
      } catch (err: any) {
        setError(err.message || "Failed to send");
        throw err;
      }
    },
    [userId, mergeMessages]
  );

  const editMessage = useCallback(
    async (eventId: string, newContent: string) => {
      if (!currentRoomRef.current) return;
      try {
        await matrixClient.editMessage(currentRoomRef.current, eventId, newContent);
        setMessages((prev) =>
          prev.map((m) =>
            m.eventId === eventId
              ? { ...m, content: newContent, edited: true }
              : m
          )
        );
      } catch (err: any) {
        setError(err.message || "Failed to edit");
        throw err;
      }
    },
    []
  );

  const deleteMessage = useCallback(
    async (eventId: string, reason?: string) => {
      if (!currentRoomRef.current) return;
      try {
        await matrixClient.deleteMessage(currentRoomRef.current, eventId, reason);
        setMessages((prev) =>
          prev.map((m) =>
            m.eventId === eventId
              ? { ...m, content: "[Message deleted]", redacted: true }
              : m
          )
        );
      } catch (err: any) {
        setError(err.message || "Failed to delete");
        throw err;
      }
    },
    []
  );

  // ── Replies ──────────────────────────────────

  const sendReply = useCallback(
    async (parentEventId: string, content: string) => {
      if (!currentRoomRef.current) throw new Error("No room selected");
      const roomId = currentRoomRef.current;
      try {
        const eventId = await matrixClient.sendReply(roomId, parentEventId, content);
        if (matrixClient.mode === "backend") {
          try {
            const result = await matrixClient.getMessages(roomId, 50);
            if (currentRoomRef.current === roomId) {
              setMessages((prev) => mergeMessages(prev, result.messages));
            }
          } catch {
            /* next poll will pick it up */
          }
        } else {
          const optimistic: MatrixMessage = {
            eventId,
            roomId,
            sender: userId || "self",
            senderDisplayName: userId || "You",
            content,
            timestamp: Date.now(),
            type: "m.room.message",
            replyTo: parentEventId,
            msgtype: "m.text",
          };
          setMessages((prev) => [...prev, optimistic]);
        }
      } catch (err: any) {
        setError(err.message || "Failed to reply");
        throw err;
      }
    },
    [userId, mergeMessages]
  );

  // ── Reactions ────────────────────────────────

  const sendReaction = useCallback(
    async (eventId: string, emoji: string) => {
      if (!currentRoomRef.current) return;
      const roomId = currentRoomRef.current;
      try {
        const reactionEventId = await matrixClient.sendReaction(roomId, eventId, emoji);
        // Optimistic update
        setReactionsState((prev) => {
          const roomReactions = { ...(prev[roomId] || {}) };
          const msgReactions = { ...(roomReactions[eventId] || {}) };
          const emojiReactions = [...(msgReactions[emoji] || [])];
          emojiReactions.push({ reactionEventId, sender: userId || "" });
          msgReactions[emoji] = emojiReactions;
          roomReactions[eventId] = msgReactions;
          return { ...prev, [roomId]: roomReactions };
        });
      } catch (err: any) {
        setError(err.message || "Failed to react");
      }
    },
    [userId]
  );

  const removeReaction = useCallback(
    async (reactionEventId: string) => {
      if (!currentRoomRef.current) return;
      try {
        await matrixClient.removeReaction(currentRoomRef.current, reactionEventId);
        // Remove from state
        const roomId = currentRoomRef.current;
        setReactionsState((prev) => {
          const roomReactions = { ...(prev[roomId] || {}) };
          for (const msgId of Object.keys(roomReactions)) {
            const msgReactions = { ...roomReactions[msgId] };
            for (const emoji of Object.keys(msgReactions)) {
              msgReactions[emoji] = msgReactions[emoji].filter(
                (r) => r.reactionEventId !== reactionEventId
              );
              if (msgReactions[emoji].length === 0) {
                delete msgReactions[emoji];
              }
            }
            roomReactions[msgId] = msgReactions;
          }
          return { ...prev, [roomId]: roomReactions };
        });
      } catch (err: any) {
        setError(err.message || "Failed to remove reaction");
      }
    },
    []
  );

  // ── Pins ─────────────────────────────────────

  const pinMessage = useCallback(
    async (eventId: string) => {
      if (!currentRoomRef.current) return;
      const roomId = currentRoomRef.current;
      try {
        await matrixClient.pinMessage(roomId, eventId);
        setPinnedState((prev) => ({
          ...prev,
          [roomId]: [...(prev[roomId] || []), eventId],
        }));
      } catch (err: any) {
        setError(err.message || "Failed to pin");
      }
    },
    []
  );

  const unpinMessage = useCallback(
    async (eventId: string) => {
      if (!currentRoomRef.current) return;
      const roomId = currentRoomRef.current;
      try {
        await matrixClient.unpinMessage(roomId, eventId);
        setPinnedState((prev) => ({
          ...prev,
          [roomId]: (prev[roomId] || []).filter((id) => id !== eventId),
        }));
      } catch (err: any) {
        setError(err.message || "Failed to unpin");
      }
    },
    []
  );

  // ── File Upload ──────────────────────────────

  const uploadAndSendFile = useCallback(
    async (file: File) => {
      if (!currentRoomRef.current) throw new Error("No room selected");
      const roomId = currentRoomRef.current;
      try {
        setIsLoading(true);
        const mxcUrl = await matrixClient.uploadFile(file);
        const eventId = await matrixClient.sendFileMessage(roomId, file, mxcUrl);

        let msgtype = "m.file";
        if (file.type.startsWith("image/")) msgtype = "m.image";
        else if (file.type.startsWith("video/")) msgtype = "m.video";
        else if (file.type.startsWith("audio/")) msgtype = "m.audio";

        const optimistic: MatrixMessage = {
          eventId,
          roomId,
          sender: userId || "self",
          senderDisplayName: userId || "You",
          content: file.name,
          timestamp: Date.now(),
          type: "m.room.message",
          msgtype,
          attachments: [
            {
              url: mxcUrl,
              filename: file.name,
              mimetype: file.type,
              size: file.size,
            },
          ],
        };
        setMessages((prev) => [...prev, optimistic]);
      } catch (err: any) {
        setError(err.message || "Failed to upload file");
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [userId]
  );

  // ── Forward ──────────────────────────────────

  const forwardMessage = useCallback(
    async (toRoomId: string, content: string) => {
      if (!currentRoomRef.current) return;
      try {
        await matrixClient.forwardMessage(currentRoomRef.current, toRoomId, content);
      } catch (err: any) {
        setError(err.message || "Failed to forward");
        throw err;
      }
    },
    []
  );

  // ── Typing ───────────────────────────────────

  const sendTyping = useCallback(
    (isTyping: boolean) => {
      if (!currentRoomRef.current) return;
      matrixClient.sendTypingIndicator(currentRoomRef.current, isTyping).catch(() => {});
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (isTyping) {
        typingTimeoutRef.current = setTimeout(() => {
          if (currentRoomRef.current) {
            matrixClient
              .sendTypingIndicator(currentRoomRef.current, false)
              .catch(() => {});
          }
        }, 4000);
      }
    },
    []
  );

  // ── Read Receipts ────────────────────────────

  const markRead = useCallback((eventId: string) => {
    if (!currentRoomRef.current) return;
    matrixClient.sendReadReceipt(currentRoomRef.current, eventId).catch(() => {});
  }, []);

  // ── Backend Polling Loop ─────────────────────

  useEffect(() => {
    if (!isLoggedIn || matrixClient.mode !== "backend") return;
    if (!currentRoomRef.current) return;

    const roomId = currentRoomRef.current;
    const poll = async () => {
      try {
        const result = await matrixClient.getMessages(roomId, 50);
        if (currentRoomRef.current !== roomId) return; // room changed
        setMessages((prev) => mergeMessages(prev, result.messages));
      } catch (err) {
        console.error("Backend poll error:", err);
      }
    };

    // Immediate first poll, then every 3 seconds
    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [isLoggedIn, currentRoom, mergeMessages]);

  // ── Sync Loop (direct mode) ─────────────────

  useEffect(() => {
    if (!isLoggedIn || matrixClient.mode !== "direct") return;

    let cancelled = false;
    syncRunningRef.current = true;
    setIsSyncing(true);

    const runSync = async () => {
      while (!cancelled) {
        try {
          const timeout = syncTokenRef.current ? 30000 : 0;
          const resp: MatrixSyncResponse = await matrixClient.sync(
            syncTokenRef.current || undefined,
            timeout
          );
          if (cancelled) break;

          syncTokenRef.current = resp.nextBatch;

          // Process joined rooms
          for (const [roomId, roomData] of Object.entries(resp.rooms.join)) {
            // Timeline events → new messages, reactions, edits, redactions
            if (currentRoomRef.current === roomId && roomData.timeline.events.length > 0) {
              const newMsgs: MatrixMessage[] = [];
              const edits: Record<string, string> = {};
              const redactions: Set<string> = new Set();
              const newReactions: Array<{
                targetEventId: string;
                emoji: string;
                sender: string;
                reactionEventId: string;
              }> = [];

              for (const ev of roomData.timeline.events) {
                if (ev.type === "m.room.message") {
                  const relatesTo = ev.content?.["m.relates_to"];
                  if (relatesTo?.rel_type === "m.replace") {
                    const targetId = relatesTo.event_id;
                    const newBody =
                      ev.content?.["m.new_content"]?.body || ev.content?.body || "";
                    edits[targetId] = newBody;
                  } else {
                    const replyTo = relatesTo?.["m.in_reply_to"]?.event_id;
                    const content = ev.content || {};
                    const msgtype = content.msgtype;

                    // Extract attachments
                    const attachments: any[] = [];
                    if (msgtype === "m.image" || msgtype === "m.file" || msgtype === "m.video" || msgtype === "m.audio") {
                      attachments.push({
                        url: content.url || "",
                        filename: content.body || "file",
                        mimetype: content.info?.mimetype || "application/octet-stream",
                        size: content.info?.size,
                        width: content.info?.w,
                        height: content.info?.h,
                        thumbnailUrl: content.info?.thumbnail_url,
                      });
                    }

                    newMsgs.push({
                      eventId: ev.event_id || "",
                      roomId,
                      sender: ev.sender || "",
                      senderDisplayName: ev.sender || "",
                      content: ev.content?.body || "",
                      timestamp: ev.origin_server_ts || Date.now(),
                      type: ev.type,
                      replyTo,
                      msgtype,
                      attachments: attachments.length > 0 ? attachments : undefined,
                    });
                  }
                } else if (ev.type === "m.room.redaction") {
                  const redactedId = (ev as any).redacts || ev.content?.redacts;
                  if (redactedId) redactions.add(redactedId);
                } else if (ev.type === "m.reaction") {
                  const relatesTo = ev.content?.["m.relates_to"];
                  if (relatesTo?.rel_type === "m.annotation") {
                    newReactions.push({
                      targetEventId: relatesTo.event_id,
                      emoji: relatesTo.key,
                      sender: ev.sender || "",
                      reactionEventId: ev.event_id || "",
                    });
                  }
                }
              }

              // Update messages
              setMessages((prev) => {
                let updated = [...prev];

                for (const [targetId, newBody] of Object.entries(edits)) {
                  updated = updated.map((m) =>
                    m.eventId === targetId
                      ? { ...m, content: newBody, edited: true }
                      : m
                  );
                }

                if (redactions.size > 0) {
                  updated = updated.map((m) =>
                    redactions.has(m.eventId)
                      ? { ...m, content: "[Message deleted]", redacted: true }
                      : m
                  );
                }

                const existingIds = new Set(updated.map((m) => m.eventId));
                for (const msg of newMsgs) {
                  if (!existingIds.has(msg.eventId)) {
                    updated.push(msg);
                  }
                }

                return updated;
              });

              // Update reactions
              if (newReactions.length > 0) {
                setReactionsState((prev) => {
                  const roomReactions = { ...(prev[roomId] || {}) };
                  for (const r of newReactions) {
                    const msgReactions = { ...(roomReactions[r.targetEventId] || {}) };
                    const emojiReactions = [...(msgReactions[r.emoji] || [])];
                    // Don't add duplicates
                    if (!emojiReactions.find((er) => er.reactionEventId === r.reactionEventId)) {
                      emojiReactions.push({
                        reactionEventId: r.reactionEventId,
                        sender: r.sender,
                      });
                    }
                    msgReactions[r.emoji] = emojiReactions;
                    roomReactions[r.targetEventId] = msgReactions;
                  }
                  return { ...prev, [roomId]: roomReactions };
                });
              }

              // Handle redactions on reactions too
              if (redactions.size > 0) {
                setReactionsState((prev) => {
                  const roomReactions = { ...(prev[roomId] || {}) };
                  for (const msgId of Object.keys(roomReactions)) {
                    const msgReactions = { ...roomReactions[msgId] };
                    let changed = false;
                    for (const emoji of Object.keys(msgReactions)) {
                      const before = msgReactions[emoji].length;
                      msgReactions[emoji] = msgReactions[emoji].filter(
                        (r) => !redactions.has(r.reactionEventId)
                      );
                      if (msgReactions[emoji].length !== before) changed = true;
                      if (msgReactions[emoji].length === 0) {
                        delete msgReactions[emoji];
                      }
                    }
                    if (changed) {
                      roomReactions[msgId] = msgReactions;
                    }
                  }
                  return { ...prev, [roomId]: roomReactions };
                });
              }
            }

            // State events → pinned messages
            if (currentRoomRef.current === roomId && roomData.state.events.length > 0) {
              for (const ev of roomData.state.events) {
                if (ev.type === "m.room.pinned_events") {
                  setPinnedState((prev) => ({
                    ...prev,
                    [roomId]: ev.content?.pinned || [],
                  }));
                }
              }
            }

            // Ephemeral events → typing, receipts
            if (currentRoomRef.current === roomId && roomData.ephemeral.events.length > 0) {
              for (const ev of roomData.ephemeral.events) {
                if (ev.type === "m.typing") {
                  const typingUserIds: string[] = ev.content?.user_ids || [];
                  setTypingState((prev) => ({
                    ...prev,
                    [roomId]: typingUserIds.filter((u) => u !== userId),
                  }));
                }
                if (ev.type === "m.receipt") {
                  const receiptContent = ev.content || {};
                  setReadReceiptState((prev) => {
                    const roomReceipts = { ...(prev[roomId] || {}) };
                    for (const [evtId, receiptData] of Object.entries(receiptContent as Record<string, any>)) {
                      const readers = receiptData?.["m.read"] || {};
                      for (const uid of Object.keys(readers)) {
                        roomReceipts[uid] = evtId;
                      }
                    }
                    return { ...prev, [roomId]: roomReceipts };
                  });
                }
              }
            }
          }
        } catch (err: any) {
          if (cancelled) break;
          console.error("Sync error:", err.message);
          await new Promise((r) => setTimeout(r, 5000));
        }
      }
      syncRunningRef.current = false;
      setIsSyncing(false);
    };

    runSync();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, userId]);

  // ── Fetch rooms on login ─────────────────────

  useEffect(() => {
    if (isLoggedIn) {
      refreshRooms();
    }
  }, [isLoggedIn, refreshRooms]);

  // ── Computed values ──────────────────────────

  const currentRoomId = currentRoomRef.current;
  const typingUsers = currentRoomId
    ? (typingState[currentRoomId] || []).filter((u) => u !== userId)
    : [];
  const readReceipts = currentRoomId
    ? readReceiptState[currentRoomId] || {}
    : {};
  const reactions = currentRoomId
    ? reactionsState[currentRoomId] || {}
    : {};
  const pinnedEventIds = currentRoomId
    ? pinnedState[currentRoomId] || []
    : [];

  // ── Context Value ────────────────────────────

  const value: MatrixContextType = {
    client: matrixClient,
    isLoggedIn,
    userId,
    currentRoom,
    currentRoomMembers,
    messages,
    rooms,
    isLoading,
    isSyncing,
    error,
    typingUsers,
    readReceipts,
    reactions,
    pinnedEventIds,
    login,
    logout,
    setCurrentRoom,
    refreshRooms,
    sendMessage,
    editMessage,
    deleteMessage,
    loadMoreMessages,
    sendReply,
    sendReaction,
    removeReaction,
    pinMessage,
    unpinMessage,
    uploadAndSendFile,
    forwardMessage,
    sendTyping,
    markRead,
  };

  return <MatrixContext.Provider value={value}>{children}</MatrixContext.Provider>;
};

// ==================== HOOK ====================

export const useMatrix = (): MatrixContextType => {
  const context = useContext(MatrixContext);
  if (!context) {
    throw new Error("useMatrix must be used within a MatrixProvider");
  }
  return context;
};

export default MatrixProvider;
